import type { IdentityReport } from '@drawing2d/core';
import { t, type Lang } from '../i18n.js';

/**
 * Every entity, followed from the structured output into the DXF.
 *
 * The accuracy figures on this page answer *how many*. They cannot answer
 * *which*, and the two come apart in the case that matters most here: an entity
 * that leaves as a `LINE` and arrives as an `LWPOLYLINE` is one entity, correct
 * in every coordinate, and a per-type figure reports it as one lost and one
 * invented. Two defects, neither real, and the actual fault nowhere on screen.
 *
 * So this says which entity and where, and it is shown beside the download
 * rather than in a report somebody reads afterwards — because the claim it makes
 * is about the file in front of them, not about a corpus.
 */
export function IdentityChain({
  report,
  lang,
}: {
  report: IdentityReport;
  lang: Lang;
}): JSX.Element {
  const rows = [
    ...report.missing.map((e) => ({ kind: 'identityMissing' as const, id: e.id, what: e.type })),
    ...report.extra.map((e) => ({ kind: 'identityExtra' as const, id: e.id, what: e.type })),
    ...report.wronglyTyped.map((c) => ({
      kind: 'identityTyped' as const,
      id: c.declared.id,
      what: `${c.declared.type} → ${c.delivered.type}`,
    })),
    ...report.misattributed.map((e) => ({ kind: 'identityPage' as const, id: e.id, what: e.type })),
  ];

  return (
    <section className="panel identity" data-ok={report.ok}>
      {/*
        Label left, value right, mono — the column's own idiom.

        The claim used to stand alone: "none missing, none added, none under
        another type", set in three lines of green inside a box. Three empty
        lists is also what a verifier that checked nothing produces, so the
        strongest statement on the surface had the least evidence under it. The
        figure is that evidence, and it belongs in the heading rather than in
        prose because the accepted direction calls this column a title block
        rather than a stack of cards.
      */}
      <h2>
        <span className="identity-label">{t('identity', lang)}</span>
        <span className="identity-figure">
          {report.followed} / {report.declared}
        </span>
      </h2>

      {report.ok ? (
        <p className="identity-ok">{t('identityOk', lang)}</p>
      ) : (
        <>
          <p className="identity-bad">
            {t('identityFaults', lang).replace('{n}', String(report.faults))}
          </p>
          <ul className="identity-faults">
            {rows.map((r) => (
              <li key={`${r.kind}-${r.id}`} data-fault={r.kind}>
                <span className="fault-kind">{t(r.kind, lang)}</span>
                <code>{r.id}</code>
                <span className="fault-what">{r.what}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/*
        Why a count could not have told them this — folded, because it is an
        argument rather than a finding and the verdict above is what has to be
        read. Unfolded it cost the convert page its two-screen budget, which is
        the U5 defect again: the thing worth reading pushed down by the
        explanation of why it is worth reading.
      */}
      <details className="fold" data-fold="identity-why">
        <summary>{t('identityWhyLabel', lang)}</summary>
        <p className="note">{t('identityWhy', lang)}</p>
      </details>
    </section>
  );
}
